import { useMemo } from 'react';
import {
  rankWith,
  ControlProps,
  isControl,
  and,
  schemaMatches,
  update,
} from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { PrincipalPicker, PrincipalEndpoints, PrincipalValue } from '../PrincipalPicker';

/**
 * JSON Forms renderer for a `principal` slot. Bound to the `{Slot}PrincipalType` anchor
 * column; reads/writes the sibling `{Slot}PrincipalId` and `{Slot}PrincipalQualifier`
 * columns. Delegates the UI to the shared <PrincipalPicker> so it behaves identically to
 * the custom OrgUnit tree panel. spec:PrincipalrefOwnership §3/§12.
 */

interface PrincipalConfig {
  typeProperty: string;
  idProperty: string;
  qualifierProperty: string;
  endpoints: { user: string; role: string; orgUnit: string };
  qualifierLookup: { endpoint: string; valueField: string; displayField: string };
  /** Form property holding the org node to pre-fill for OrgRole/OrgUnit (e.g. `ownerOrgUnitId`,
   *  or `id` when the edited entity IS an OrgUnit). Absent → no prefill. */
  defaultNodeProperty?: string;
  /** Guardrail: when set, only these PrincipalType codes are offered (e.g. ['ORG_ROLE']). */
  allowedPrincipalTypes?: string[];
}

const PrincipalRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, required, errors, schema, visible } = props;
  const ctx = useJsonForms();
  const formData = ctx.core?.data || {};

  const cfg: PrincipalConfig | undefined = (schema as any)?.['x-principal'];
  const enumValues: string[] = (schema as any)?.enum || [];
  const enumNames: string[] = (schema as any)?.enumNames || [];

  const { idPath, qualifierPath } = useMemo(() => {
    const segs = (path || '').split('.');
    segs.pop();
    const parent = segs.join('.');
    const id = cfg?.idProperty ?? '';
    const q = cfg?.qualifierProperty ?? '';
    return {
      idPath: parent ? `${parent}.${id}` : id,
      qualifierPath: parent ? `${parent}.${q}` : q,
    };
  }, [path, cfg?.idProperty, cfg?.qualifierProperty]);

  const endpoints: PrincipalEndpoints | undefined = useMemo(() => cfg ? {
    user: cfg.endpoints.user,
    role: cfg.endpoints.role,
    orgUnit: cfg.endpoints.orgUnit,
    qualifier: cfg.qualifierLookup,
  } : undefined, [cfg]);

  const typeOptions = useMemo(
    () => {
      const all = enumValues.map((code, i) => ({ code, name: enumNames[i] || code }));
      const allow = cfg?.allowedPrincipalTypes;
      return allow && allow.length > 0 ? all.filter((o) => allow.includes(o.code)) : all;
    },
    [enumValues, enumNames, cfg?.allowedPrincipalTypes],
  );

  if (!visible || !cfg) return null;

  const value: PrincipalValue = {
    type: (data as string) || undefined,
    id: formData[cfg.idProperty],
    qualifier: formData[cfg.qualifierProperty],
  };

  const onChange = (next: PrincipalValue) => {
    handleChange(path, next.type ?? undefined);
    ctx.dispatch?.(update(idPath, () => next.id ?? undefined));
    ctx.dispatch?.(update(qualifierPath, () => next.qualifier ?? undefined));
  };

  return (
    <PrincipalPicker
      value={value}
      onChange={onChange}
      endpoints={endpoints}
      typeOptions={typeOptions.length > 0 ? typeOptions : undefined}
      required={required}
      // Drop the trailing " Type" (sv: " – typ") from the anchor column's label so the composite
      // reads "Owner"/"Ägare" (not "Owner Type"/"Ägare – typ") above the inner Type/Function/node
      // controls. The suffix follows the DisplayName convention of the active language.
      label={(label || '').replace(/ Type$/, '').replace(/ – typ$/i, '')}
      error={errors}
      // Pre-fill the OrgRole/OrgUnit node from the configured owning-org-unit property:
      // `ownerOrgUnitId` for entities that own a node (PlanHeader), `id` when the entity IS an
      // OrgUnit ("Controller of THIS unit"), or nothing when there's no owning org unit
      // (PlanScenario) — never the record's own id, which would render as a bogus GUID.
      defaultNodeId={cfg.defaultNodeProperty ? (formData as any)?.[cfg.defaultNodeProperty] : undefined}
    />
  );
};

export const PrincipalRenderer = withJsonFormsControlProps(PrincipalRendererComponent);

/** Tester: matches the principal anchor control (schema.format === 'principal'). */
export const principalRendererTester = rankWith(
  10,
  and(isControl, schemaMatches((schema) => (schema as any).format === 'principal')),
);

export const principalRendererEntry = {
  tester: principalRendererTester,
  renderer: PrincipalRenderer,
};
